import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, Radio, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { TurfTalk, AuthExpiredError } from "@/api/client";
import LiveStage from "@/components/live/LiveStage";
import VoiceRehearsal from "@/components/live/VoiceRehearsal";
import { trackHostStart, trackHostEnd } from "@/lib/telemetry";

// Honest connection lifecycle: token → connect → media. Never shows a live
// stage unless a token was issued AND the LiveKit room reports Connected.
// Track current visits so a late token from an abandoned visit cannot release
// the same owner's seat after they have already returned to that room.
const roomVisits = new Map();
export default function LiveRoomPage() {
  const { id } = useParams();
  return <LiveRoomSession key={id} id={id} />;
}

function LiveRoomSession({ id }) {
  const navigate = useNavigate();
  const [stream, setStream] = useState(null);
  const [tok, setTok] = useState(null); // { server_url, participant_token, can_publish, role, media_mode }
  const [phase, setPhase] = useState("loading"); // loading | joining | connected | error | ended
  const [errMsg, setErrMsg] = useState("");
  const [count, setCount] = useState(0);
  const [microphoneReady, setMicrophoneReady] = useState(false);
  const leftRef = useRef(false);
  const everConnectedRef = useRef(false);
  const hostStartMsRef = useRef(0);
  const hostedRef = useRef(false);
  const streamRef = useRef(null);
  const generation = useRef(0);
  const visitRef = useRef(null);
  const joining = useRef(false);

  const leave = useCallback(async (visit = visitRef.current) => {
    if (!visit || !visit.seat || visit.left || roomVisits.get(id) !== visit) return;
    visit.left = true; leftRef.current = true;
    if (hostedRef.current) { trackHostEnd(streamRef.current?.game_id, id, Date.now() - hostStartMsRef.current); hostedRef.current = false; }
    try { await TurfTalk.liveLeave(id); } catch (_) {}
    finally { if (!visit.active && roomVisits.get(id) === visit) roomVisits.delete(id); }
  }, [id]);

  const join = useCallback(async () => {
    if (joining.current) return;
    joining.current = true;
    const attempt = ++generation.current;
    const visit = visitRef.current;
    const current = () => visit?.active && !visit.left && generation.current === attempt;
    setPhase("joining"); setMicrophoneReady(false); setErrMsg(""); setTok(null);
    try {
      const meta = await TurfTalk.liveGet(id);
      if (!current()) return;
      const details = meta?.livestream || meta?.stream || meta;
      if (!details || typeof details !== "object") throw new Error("Room details are unavailable. Please try again.");
      if (["ended", "canceled", "cancelled", "terminated", "failed"].includes(details.status)) throw new Error("This stream is not live.");
      streamRef.current = details; setStream(details);
      visit.pendingToken = true;
      const t = await TurfTalk.liveToken(id);
      // Token issuance allocates a server seat even before RTC connects.
      visit.seat = true;
      if (!current()) { await leave(visit); return; }
      if (!t || !t.participant_token || !t.server_url) throw new Error("The streaming provider did not return a token.");
      setTok({ ...t, joinAttempt: attempt, media_mode: details.media_mode || t.media_mode || "audio" });
      setPhase("connecting");
    } catch (e) {
      if (!current()) return;
      if (e instanceof AuthExpiredError) return;
      const msg = e?.message || "Could not join this stream.";
      setErrMsg(msg);
      setPhase(/not live/i.test(msg) ? "ended" : "error");
    } finally {
      visit.pendingToken = false;
      if (generation.current === attempt) joining.current = false;
      if (!visit.active && roomVisits.get(id) === visit) roomVisits.delete(id);
    }
  }, [id, leave]);

  useEffect(() => {
    const visit = {active: true, seat: false, left: false};
    visitRef.current = visit; roomVisits.set(id, visit);
    joining.current = false;
    join();
    return () => {
      visit.active = false;
      if (visit.seat) leave(visit);
      else if (!visit.pendingToken && roomVisits.get(id) === visit) roomVisits.delete(id);
    };
  }, [id, join, leave]);

  // Safeguard: while connected, poll lifecycle so viewers drop when the host
  // ends/cancels (backend marks ended; this forces an honest local teardown).
  useEffect(() => {
    if (phase !== "connecting" && phase !== "connected" && phase !== "error") return;
    let active = true, requested = 0, applied = 0;
    const iv = setInterval(async () => {
      try {
        const sequence = ++requested;
        const m = await TurfTalk.liveGet(id);
        if (!active || sequence <= applied) return;
        const details = m?.livestream || m?.stream || m;
        if (!details || typeof details !== "object" || (details.id && details.id !== id)) return;
        applied = sequence;
        const st = details.status;
        streamRef.current = details; setStream(details);
        if (["ended", "canceled", "cancelled", "terminated", "failed"].includes(st)) { active = false; clearInterval(iv); setErrMsg("This stream has ended."); setPhase("ended"); leave(); }
      } catch (_) {}
    }, 5000);
    return () => { active = false; clearInterval(iv); };
  }, [phase, id, leave]);

  // Best-effort leave if the tab is closed mid-stream.
  useEffect(() => {
    const h = () => { navigator.sendBeacon && leave(); };
    window.addEventListener("beforeunload", h);
    return () => { window.removeEventListener("beforeunload", h); };
  }, [leave]);

  const exit = async () => {
    if (visitRef.current) visitRef.current.active = false;
    ++generation.current;
    await leave(); navigate("/live");
  };
  const endStream = async () => {
    try { await TurfTalk.liveEnd(id); toast.success("Stream ended"); } catch (e) { toast.error(e.message); }
    await leave(); navigate("/live");
  };

  const onDisconnected = () => {
    if (leftRef.current) return;
    setErrMsg("Disconnected from live media. You can try joining again; room status will be checked first.");
    setPhase("error");
  };
  const onError = (e) => {
    const m = String(e?.message || e || "");
    let friendly = "Live media error.";
    if (/permission|NotAllowed|denied/i.test(m)) friendly = "Camera/microphone permission was denied. Allow access in your browser and rejoin.";
    else if (/NotFound|device/i.test(m)) friendly = "No camera or microphone was found on this device.";
    else if (/NotReadable|in use/i.test(m)) friendly = "Your camera/microphone is already in use by another app.";
    setErrMsg(friendly); setPhase("error");
    toast.error(friendly);
  };

  const isHost = tok?.role === "host" || stream?.is_host;
  // An SDK callback from the stage torn down for a retry must not modify the
  // replacement stage, its count, or its readiness.
  const currentStage = () => visitRef.current?.active && !leftRef.current && tok?.joinAttempt === generation.current;

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6">
      <div className="flex items-center justify-between mb-4">
        <button onClick={exit} data-testid="live-room-back" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-sm"><ArrowLeft size={16} /> Fan Streams</button>
        <div className="flex items-center gap-2">
          {phase === "connected" && <span className="text-[11px] px-2 py-0.5 rounded bg-[#D92D20]/15 text-[#ff6b60] font-display tracking-wide" data-testid="live-badge">LIVE</span>}
          <span className="text-[11px] text-white/50 tabnums" data-testid="live-viewer-count">{count} in room</span>
          {isHost
            ? <button onClick={endStream} data-testid="live-end" className="inline-flex items-center gap-2 px-3 h-9 rounded-lg bg-[#3a1e1e] text-[#ff8a80] text-sm">End stream</button>
            : <button onClick={exit} data-testid="live-leave" className="inline-flex items-center gap-2 px-3 h-9 rounded-lg tt-panel text-white/70 hover:text-white text-sm"><LogOut size={14} /> Leave</button>}
        </div>
      </div>

      <div className="mb-3">
        <h1 className="font-display text-2xl text-white">{stream?.title || "Fan stream"}</h1>
        <div className="text-white/45 text-xs mt-0.5 flex gap-2">
          <span className="uppercase">{tok?.media_mode || stream?.media_mode || "video"}</span>
          {stream?.host_handle && <span>· host @{stream.host_handle}</span>}
          {tok && <span>· {tok.can_publish ? "publisher" : "viewer"}</span>}
        </div>
        <p className="text-[11px] text-white/35 mt-1">Turf Talk is not recording this stream.</p>
      </div>

      {stream?.audience === "private" && <a className="block underline text-white/70 mb-3" href={`/rehearsal/${encodeURIComponent(id)}`} target="_blank" rel="noopener noreferrer">Private rehearsal setup and co-host approval (opens in a new tab)</a>}
      {phase === "connected" && stream?.audience === "private" && ["host", "cohost"].includes(tok?.role) &&
        <VoiceRehearsal key={id} microphoneReady={microphoneReady} streamId={id} owner={stream?.is_host === true} productCohost={tok?.role === "cohost"} enabled={process.env.REACT_APP_VOICE_REHEARSAL_ENABLED === "true"} />}
      <div data-testid="live-stage-shell" className="relative rounded-xl mt-3" style={{ minHeight: 420 }}>
        {phase === "joining" || phase === "loading" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black text-white/60 gap-2" data-testid="live-joining"><Loader2 className="animate-spin" /> Requesting access & connecting…</div>
        ) : phase === "error" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black text-center px-6 gap-3" data-testid="live-error">
            <ShieldAlert className="text-[#F4B740]" />
            <p className="text-white/80 text-sm max-w-sm">{errMsg}</p>
            <button onClick={join} className="px-4 h-9 rounded-lg tt-panel-3 text-white text-sm">Try again</button>
          </div>
        ) : phase === "ended" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black text-center px-6 gap-3" data-testid="live-ended">
            <Radio className="text-white/40" />
            <p className="text-white/70 text-sm">{errMsg || "This stream has ended."}</p>
            <button onClick={() => navigate("/live")} className="px-4 h-9 rounded-lg tt-panel-3 text-white text-sm">Back to Fan Streams</button>
          </div>
        ) : tok && (phase === "connecting" || phase === "connected") ? (
          <LiveStage
            fixedInputDiagnostic={false}
            onMicrophoneReady={setMicrophoneReady}
            serverUrl={tok.server_url}
            token={tok.participant_token}
            canPublish={!!tok.can_publish}
            showAiAvatar={stream?.audience === "private"}
            aiCharacterIds={stream?.ai_character_ids || ["bruce"]}
            mediaMode={tok.media_mode || "video"}
            onConnected={() => { if (!currentStage()) return; everConnectedRef.current = true; setPhase("connected"); if (tok?.can_publish && !hostedRef.current) { hostedRef.current = true; hostStartMsRef.current = Date.now(); trackHostStart(stream?.game_id, id); } }}
            onDisconnected={() => { if (currentStage()) onDisconnected(); }}
            onError={e => { if (currentStage()) onError(e); }}
            onCount={n => { if (currentStage()) setCount(n); }}
          />
        ) : null}
      </div>

    </div>
  );
}
